import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePreviousDistinct } from "react-use";

export interface ProductsComparatorContextProps {
  products: Array<Queries.ProductDataFragment | null>;
  maxProducts: number;
  toggleProduct: (product: Queries.ProductDataFragment) => void;
  resetProducts: () => void;
  isPanelOpen: boolean;
  setPanelOpen: Dispatch<SetStateAction<boolean>>;
  selectedProductsCount: number;
  isProductSelected: (product: Queries.ProductDataFragment) => boolean;
}

export const ProductsComparatorContext = createContext<ProductsComparatorContextProps | null>(null);

export interface ProductsComparatorProviderProps {
  maxProducts: number;
  expandOnAddProduct?: boolean;
}

export function ProductsComparatorProvider({
  maxProducts = 3,
  expandOnAddProduct = true,
  children,
}: PropsWithChildren<ProductsComparatorProviderProps>) {
  const [products, setProducts] = useState<Array<Queries.ProductDataFragment>>([]);
  const selectedProductsCount = products.filter(Boolean).length;
  const [isPanelOpen, setPanelOpen] = useState(false);

  const toggleProduct = useCallback(
    (product: Queries.ProductDataFragment) => {
      setProducts((products) => {
        const hasProduct = products.find((p) => p.id === product.id);
        if (hasProduct) {
          return products.filter((p) => p.id !== product.id);
        } else {
          return [...products, product].slice(-maxProducts);
        }
      });
    },
    [maxProducts]
  );

  const previousSelectedProductsCount = usePreviousDistinct(selectedProductsCount);
  useEffect(() => {
    if (selectedProductsCount === 0) setPanelOpen(false);
    if (
      selectedProductsCount > 1 &&
      selectedProductsCount > (previousSelectedProductsCount ?? 0) &&
      expandOnAddProduct
    ) {
      setPanelOpen(true);
    }
  }, [expandOnAddProduct, selectedProductsCount, previousSelectedProductsCount]);

  const isProductSelected = useCallback(
    (product: Queries.ProductDataFragment) => {
      return Boolean(products.find((p) => p.id === product.id));
    },
    [products]
  );

  const resetProducts = useCallback(() => {
    setProducts([]);
  }, []);

  const value = useMemo<ProductsComparatorContextProps>(
    () => ({
      products: new Array(maxProducts).fill(null).map((_, i) => products[i] ?? null),
      maxProducts,
      resetProducts,
      toggleProduct,
      selectedProductsCount,
      isPanelOpen,
      setPanelOpen,
      isProductSelected,
    }),
    [
      resetProducts,
      toggleProduct,
      isPanelOpen,
      setPanelOpen,
      selectedProductsCount,
      maxProducts,
      isProductSelected,
      products,
    ]
  );

  return (
    <ProductsComparatorContext.Provider value={value}>
      {children}
    </ProductsComparatorContext.Provider>
  );
}

export function useProductsComparator() {
  return useContext(ProductsComparatorContext)!;
}
